import {
  useCallback,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useParams } from 'react-router-dom';
import type { BrowserAgentSitesViewSite } from '@kirin/api-client/contracts/shadow-ai';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  DataTable,
  DEFAULT_PAGE_SIZE_OPTIONS,
  type DataFetchParams,
} from '@/components/DataTable';
import { TABLE_IDS } from '@/components/DataTable/tableIds';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { useSetBrowserAgentDecision } from '@/hooks/api/useShadowAiTools';
import { fetchBrowserAgentSites } from './api';
import { BrowserAgentSiteUsersModal } from './BrowserAgentSiteUsersModal';
import { EnforcementToggle, UsersCount } from './cells';
import { decidedBy } from './cellUtils';
import { createBrowserAgentSitesColumns } from './columns';
import { AGENT_TYPE_META, type Enforcement } from './mockData';

/**
 * Browser Agent Sites view (KIR-4703 / KIR-4704, spec 70509).
 *
 * On the shared DataTable (product review round 2): no agent-type tabs — the
 * Agent and Via columns filter instead — and every column sorts or filters
 * server-side. Enforcement persists via the decision mutation; the Users count
 * opens the users behind the row.
 */

function rowKey(site: BrowserAgentSitesViewSite): string {
  return `${site.siteHost}::${site.agentType}`;
}

function Stat({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="text-sm font-semibold">{children}</div>
    </div>
  );
}

function DetailPanel({
  site,
  onEnforcementChange,
  onUsersClick,
  disabled,
}: {
  site: BrowserAgentSitesViewSite;
  onEnforcementChange: (next: Enforcement) => void;
  onUsersClick: (site: BrowserAgentSitesViewSite) => void;
  disabled: boolean;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  const meta = AGENT_TYPE_META[site.agentType];
  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Enforcement
        </p>
        <EnforcementToggle
          options={meta.options}
          value={site.enforcement}
          onChange={onEnforcementChange}
          disabled={disabled}
          size="lg"
        />
        <p className="mt-2 text-sm text-muted-foreground">{meta.description}</p>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Approval
        </p>
        <span className="text-sm text-muted-foreground">
          {site.approval && site.enforcement
            ? decidedBy(site.enforcement, site.approval, timeZone)
            : 'Not reviewed yet'}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-10 gap-y-2">
        <Stat label="Visits (30d)">{site.visits30d}</Stat>
        <Stat label="Users">
          <UsersCount site={site} onClick={onUsersClick} />
        </Stat>
        <Stat label="Last seen">
          <RelativeTimeWithTooltip date={site.lastSeen} />
        </Stat>
        <Stat label="Via">{site.reachedVia}</Stat>
      </div>
    </div>
  );
}

const DEFAULT_PAGE_SIZE = 25;

export default function BrowserAgentSitesPage(): JSX.Element {
  const { organizationId = '' } = useParams<{ organizationId?: string }>();
  const decisionMutation = useSetBrowserAgentDecision(organizationId);
  // Owners and managers decide; support (and anyone while roles load) sees the
  // controls read-only — the backend rejects their writes anyway (KIR-4703).
  const writeGate = useOrgWriteGate(organizationId);
  const controlsDisabled =
    writeGate.hidden ||
    writeGate.disabled ||
    writeGate.isLoading ||
    decisionMutation.isPending;

  // Undo asks for confirmation first, like the app's delete confirmations.
  const [pendingUndo, setPendingUndo] = useState<{
    site: BrowserAgentSitesViewSite;
    enforcement: Enforcement;
  } | null>(null);
  const [usersSite, setUsersSite] = useState<BrowserAgentSitesViewSite | null>(
    null
  );

  const { mutate } = decisionMutation;
  // Persist a decision (or clear it with action=null) to the backend.
  const applyDecision = useCallback(
    (site: BrowserAgentSitesViewSite, action: Enforcement | null): void =>
      mutate({ agentType: site.agentType, host: site.siteHost, action }),
    [mutate]
  );

  const columns = useMemo(
    () =>
      createBrowserAgentSitesColumns({
        organizationId,
        controlsDisabled,
        onDecide: applyDecision,
        onUndo: (site, enforcement) => setPendingUndo({ site, enforcement }),
        onUsersClick: setUsersSite,
      }),
    [organizationId, controlsDisabled, applyDecision]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchBrowserAgentSites(organizationId, params, signal),
    [organizationId]
  );

  // Keyed under the view's prefix so a decision's invalidation refreshes the
  // page on screen.
  const queryKeyFn = useCallback(
    (params: DataFetchParams) => [
      ...queryKeys.shadowAi.browserAgentSites(organizationId),
      {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      },
    ],
    [organizationId]
  );

  const queryConfig = useMemo(
    () => ({ staleTime: 30000, refetchOnWindowFocus: false }),
    []
  );

  return (
    <div className="flex flex-col gap-4">
      {/* The title comes from the org PageHeader (KIR-4703) — no second h1 here. */}
      <p className="text-sm text-muted-foreground">
        See which sites AI agents reach, and what Kirin has done with each.
        Today we cover Claude Code and Chromium-based browsers, including
        Chrome, Edge, Brave and Vivaldi. VS Code, Codex, and Cursor are coming
        soon.
      </p>

      <DataTable<BrowserAgentSitesViewSite>
        id={TABLE_IDS.ORGANIZATION.BROWSER_AGENT_SITES}
        mode="server"
        columns={columns}
        initialState={{
          sort: { field: 'lastSeen', order: SortOrder.Desc },
          pagination: { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
        }}
        search={{ placeholder: 'Search sites', className: 'w-[260px]' }}
        features={{
          enableSorting: true,
          enableRowExpansion: true,
          enableUrlState: true,
        }}
        rowExpansion={{
          getRowId: rowKey,
          render: site => (
            <DetailPanel
              site={site}
              disabled={controlsDisabled}
              onEnforcementChange={next => applyDecision(site, next)}
              onUsersClick={setUsersSite}
            />
          ),
          contentClassName: 'bg-muted/30',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
          highlightOnExpand: true,
        }}
        paginationOptions={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        queryConfig={queryConfig}
        countScope={{ organizationId }}
        noDataLabel="No browser-agent sites yet."
        errorBoundaryLabel="Something went wrong in the Browser Agent Sites table"
      />

      <p className="text-xs text-muted-foreground">
        Enforcement options depend on the agent type. Approval shows who signed
        off and when.
      </p>

      <ConfirmationDialog
        isOpen={pendingUndo !== null}
        onClose={() => setPendingUndo(null)}
        onConfirm={() => {
          if (pendingUndo) applyDecision(pendingUndo.site, null);
          setPendingUndo(null);
        }}
        title="Undo this decision?"
        description="This clears the decision. The site returns to Not reviewed."
        confirmLabel="Undo"
        variant={
          pendingUndo?.enforcement === 'monitor' ? 'default' : 'destructive'
        }
      />

      <BrowserAgentSiteUsersModal
        organizationId={organizationId}
        site={usersSite}
        onClose={() => setUsersSite(null)}
      />
    </div>
  );
}

BrowserAgentSitesPage.displayName = 'BrowserAgentSitesPage';
